import { useMemo, useState } from "react";
import { gltfLoader } from "@/filestore";
import { FileLoader } from "three";
import * as utils from "three/examples/jsm/utils/SkeletonUtils.js";
import { convertGltf } from "../../utils/convertGltf";
// global cache outside of react
export const modelCache = {};
export const createModelLoadPromise = (url, onError) => {
    const modelData = modelCache[url];
    if (modelData?.status === 'pending')
        return modelData.promise;
    if (modelData?.status === 'complete')
        return Promise.resolve({ url, arrayBuffer: modelData?.arrayBuffer });
    // we are using the custom gltfloader which always uses textureLoader instead of 
    // the imageBitmapLoader. Latter does not always release memory coorectly leading resulting in app crashing
    // (see https://discourse.threejs.org/t/error-with-the-gltfloader-unable-to-load-texture-blob/32465)
    const loadPromise = new Promise(res => {
        new FileLoader()
            .setResponseType('arraybuffer')
            .load(url, arrayBuffer => {
            if (typeof arrayBuffer === 'string') {
                modelCache[url] = {
                    status: 'error',
                    error: new Error("model load error")
                };
                onError?.();
                return;
            }
            gltfLoader.parse(arrayBuffer, '', gltf => {
                if (modelCache[url]?.status !== 'complete') {
                    modelCache[url] = {
                        status: 'complete',
                        gltf,
                        parsedData: convertGltf(gltf),
                        arrayBuffer
                    };
                }
                res({ arrayBuffer, url });
            }, () => {
                modelCache[url] = {
                    status: 'error',
                    error: new Error("model load error")
                };
                onError?.();
            });
        }, undefined, () => {
            modelCache[url] = {
                status: 'error',
                error: new Error("model load error")
            };
            onError?.();
        });
    });
    modelCache[url] = {
        status: 'pending',
        promise: loadPromise
    };
    return loadPromise;
};
export const preloadGltf = (urls) => {
    // create array of promises
    const promises = [];
    for (let i = 0; i < urls.length; i++) {
        const url = urls[i];
        if (!!modelCache[url]?.status)
            continue;
        const modelLoadPromise = createModelLoadPromise(url);
        promises.push(modelLoadPromise);
    }
    return promises.length
        ? Promise.all([...promises])
        : new Promise((res) => res(null));
};
export const useGLTFArrayBufferLoader = (url, options) => {
    const { castShadow, receiveShadow } = options ?? {};
    // state used to trigger rerender in order to throw async errors into render phase
    const [_, triggerRender] = useState(0);
    const modelData = modelCache[url];
    // return model immediately if in cache
    if (modelData?.status === 'complete') {
        const { scene: modelScene, animations, } = modelData.gltf;
        return useMemo(() => {
            // use Skeleton utils to safely clone skinned mesh (and other) models
            const clonedScene = utils.clone(modelScene);
            let isSkinned = false;
            // add render order to mesh if material(s) are transparent
            clonedScene.traverse(n => {
                const node = n;
                if (node.isSkinnedMesh || node.isMesh) {
                    if (node.isSkinnedMesh)
                        isSkinned = true;
                    if (castShadow)
                        node.castShadow = true;
                    if (receiveShadow)
                        node.receiveShadow = true;
                }
            });
            clonedScene.updateMatrixWorld(true);
            return { scene: clonedScene, animations, isSkinned, arrayBuffer: modelData.arrayBuffer };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [url, modelScene, animations, castShadow, receiveShadow]);
    }
    // if async error throw error into render phase (triggered via triggerRender)
    if (modelData?.status === 'error') {
        throw modelData.error;
        // delete globalErrorCache[url];
        // throw error;
    }
    // throw promise for suspense to catch and also trigger re-render once error
    // occurs to throw async error in new render phase
    throw createModelLoadPromise(url, () => {
        console.log("prelading error");
        triggerRender((prevState) => prevState + 1);
    });
};
